import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import type {} from '@redux-devtools/extension' // required for devtools typing
import {
  listOutlineTemplates,
  createOutlineTemplate,
  updateOutlineTemplate,
  deleteOutlineTemplate,
  duplicateOutlineTemplate,
} from '@/lib/api'
import type { OutlineTemplate, OutlineTemplateSection } from '@/lib/api'

// ===================
// Types
// ===================

interface CreateOutlineTemplateInput {
  name: string
  description?: string
  sections: OutlineTemplateSection[]
  visibility?: 'private' | 'shared'
}

interface UpdateOutlineTemplateInput {
  name?: string
  description?: string | null
  sections?: OutlineTemplateSection[]
  visibility?: 'private' | 'shared'
}

interface OutlineTemplatesState {
  // State
  templates: OutlineTemplate[]
  isLoading: boolean
  error: string | null
  lastFetchedAt: number | null

  // Actions
  fetchTemplates: (force?: boolean) => Promise<void>
  createTemplate: (data: CreateOutlineTemplateInput) => Promise<OutlineTemplate>
  updateTemplate: (id: string, data: UpdateOutlineTemplateInput) => Promise<void>
  deleteTemplate: (id: string) => Promise<void>
  duplicateTemplate: (id: string) => Promise<OutlineTemplate>
  clearError: () => void
  reset: () => void
}

// ===================
// Constants
// ===================

const STALE_THRESHOLD_MS = 5 * 60 * 1000 // 5 minutes

// ===================
// Store
// ===================

export const useOutlineTemplatesStore = create<OutlineTemplatesState>()(
  devtools(
    (set, get) => ({
      templates: [],
      isLoading: false,
      error: null,
      lastFetchedAt: null,

      fetchTemplates: async (force = false) => {
        const { lastFetchedAt, isLoading } = get()
        if (isLoading) return

        if (!force && lastFetchedAt && Date.now() - lastFetchedAt < STALE_THRESHOLD_MS) {
          return
        }

        set({ isLoading: true, error: null })
        try {
          const templates = await listOutlineTemplates()
          set({ templates, lastFetchedAt: Date.now() })
        } catch (err) {
          set({ error: err instanceof Error ? err.message : 'Failed to load templates' })
        } finally {
          set({ isLoading: false })
        }
      },

      createTemplate: async (data) => {
        set({ error: null })
        try {
          const template = await createOutlineTemplate(data)
          set((state) => ({
            templates: [template, ...state.templates],
            lastFetchedAt: Date.now(),
          }))
          return template
        } catch (err) {
          const message = err instanceof Error ? err.message : 'Failed to create template'
          set({ error: message })
          throw err
        }
      },

      updateTemplate: async (id, data) => {
        set({ error: null })
        try {
          const updated = await updateOutlineTemplate(id, data)
          set((state) => ({
            templates: state.templates.map((t) => (t.id === id ? updated : t)),
          }))
        } catch (err) {
          const message = err instanceof Error ? err.message : 'Failed to update template'
          set({ error: message })
          throw err
        }
      },

      deleteTemplate: async (id) => {
        set({ error: null })
        try {
          await deleteOutlineTemplate(id)
          set((state) => ({
            templates: state.templates.filter((t) => t.id !== id),
          }))
        } catch (err) {
          const message = err instanceof Error ? err.message : 'Failed to delete template'
          set({ error: message })
          throw err
        }
      },

      duplicateTemplate: async (id) => {
        set({ error: null })
        try {
          const duplicate = await duplicateOutlineTemplate(id)
          set((state) => ({
            templates: [duplicate, ...state.templates],
            lastFetchedAt: Date.now(),
          }))
          return duplicate
        } catch (err) {
          const message = err instanceof Error ? err.message : 'Failed to duplicate template'
          set({ error: message })
          throw err
        }
      },

      clearError: () => set({ error: null }),

      reset: () =>
        set({
          templates: [],
          isLoading: false,
          error: null,
          lastFetchedAt: null,
        }),
    }),
    { name: 'outline-templates-store' },
  ),
)
